import React, { useEffect, useState } from "react";
import { ShieldCheck, Mail, ArrowRight, Lock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import Logo from "./Logo";
import { BRAND } from "../mock";
import { apiSubscribe } from "../lib/api";

const STORAGE_KEY = "cw_age_verified";
const EMAIL_KEY = "cw_email_subscribed";

// Returning visitors who have verified within the last 30 days skip the gate.
// New visitors AND visits older than 30 days re-confirm — balances conversion + compliance.
const VERIFICATION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const AgeVerificationModal = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("verify"); // verify | email | done
  const [email, setEmail] = useState("");

  useEffect(() => {
    const verifiedAt = localStorage.getItem(STORAGE_KEY);
    const isStillValid =
      verifiedAt && Date.now() - new Date(verifiedAt).getTime() < VERIFICATION_TTL_MS;
    if (!isStillValid) {
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  const handleVerify = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    const alreadySubscribed = localStorage.getItem(EMAIL_KEY);
    if (alreadySubscribed) {
      setOpen(false);
    } else {
      setStep("email");
    }
  };

  const handleDecline = () => {
    window.location.href = "https://www.google.com";
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    try {
      await apiSubscribe(email, "popup");
    } catch (_) { /* non-fatal */ }
    localStorage.setItem(EMAIL_KEY, email);
    toast.success("Welcome! Your 25% off coupon has been emailed.");
    setStep("done");
    setTimeout(() => setOpen(false), 2400);
  };

  const handleSkip = () => {
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="absolute inset-0 pointer-events-none age-grid-bg" />

      <div className="relative w-full max-w-md bg-white border border-gray-200 shadow-2xl">
        {/* top accent bar */}
        <div className="h-1 bg-[#b91c1c]" />

        <div className="p-8 sm:p-10">
          {step === "verify" && (
            <div className="text-center">
              <div className="flex justify-center mb-5">
                <Logo size={64} />
              </div>
              <div className="flex items-center gap-3 justify-center mb-4">
                <div className="h-px w-8 bg-[#b91c1c]" />
                <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">
                  Age Verification
                </span>
                <div className="h-px w-8 bg-[#b91c1c]" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.1]">
                Are you 18<span className="text-[#b91c1c]">+</span>?
              </h2>
              <p className="text-[14px] text-gray-500 mt-4 leading-relaxed font-light">
                {BRAND.name} products are intended <strong className="text-[#0a0a0a] font-medium">for laboratory research use only</strong>. You must be 18 years or older to enter this site.
              </p>
              <div className="mt-7 grid grid-cols-2 gap-3">
                <Button
                  onClick={handleDecline}
                  variant="outline"
                  className="h-12 rounded-none border-gray-300 text-gray-700 text-[12px] font-semibold uppercase tracking-[0.18em] hover:bg-gray-100"
                >
                  I'm Under 18
                </Button>
                <Button
                  onClick={handleVerify}
                  className="h-12 rounded-none bg-[#0a0a0a] text-white text-[12px] font-semibold uppercase tracking-[0.18em] hover:bg-[#1a1a1a]"
                >
                  I'm 18 or Older
                </Button>
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-6">
                Not for human consumption · Research use only
              </p>
            </div>
          )}

          {step === "email" && (
            <div className="text-center">
              <div className="flex items-center gap-3 justify-center mb-4">
                <div className="h-px w-8 bg-[#b91c1c]" />
                <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">
                  Welcome Offer
                </span>
                <div className="h-px w-8 bg-[#b91c1c]" />
              </div>
              <h2 className="font-display text-3xl sm:text-[40px] font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
                Save <span className="text-[#b91c1c]">25%</span><br />
                <span className="font-serif italic font-normal">on your first order.</span>
              </h2>
              <p className="text-[14px] text-gray-500 mt-4 leading-relaxed font-light max-w-sm mx-auto">
                Join 1,200+ verified researchers. Enter your email to get an exclusive 25% off code delivered straight to your inbox.
              </p>
              <form onSubmit={handleEmail} className="mt-7 space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="h-12 pl-9 rounded-none border-gray-300 focus-visible:ring-[#0a0a0a]"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-none btn-red text-[12px] font-semibold uppercase tracking-[0.18em]"
                >
                  Claim My 25% Off <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
              <button
                onClick={handleSkip}
                className="mt-4 text-[11px] uppercase tracking-[0.18em] text-gray-400 hover:text-[#0a0a0a]"
              >
                No thanks, take me to the site
              </button>
              <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-3 gap-4 text-center">
                <div>
                  <ShieldCheck className="h-4 w-4 text-[#b91c1c] mx-auto mb-1" />
                  <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-gray-500">Lab Tested</div>
                </div>
                <div>
                  <Lock className="h-4 w-4 text-[#b91c1c] mx-auto mb-1" />
                  <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-gray-500">Private</div>
                </div>
                <div>
                  <Mail className="h-4 w-4 text-[#b91c1c] mx-auto mb-1" />
                  <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-gray-500">No Spam</div>
                </div>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-8">
              <div className="h-14 w-14 mx-auto rounded-full bg-[#b91c1c]/10 flex items-center justify-center mb-4">
                <ShieldCheck className="h-7 w-7 text-[#b91c1c]" />
              </div>
              <h2 className="font-display text-2xl font-semibold text-[#0a0a0a] tracking-tight">
                You're In.
              </h2>
              <p className="text-[14px] text-gray-500 mt-2 font-light">
                Check your inbox — your 25% off code is on the way.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgeVerificationModal;
